import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { dashboardProjectsSelector, filtersDashboardProjectUsedSelector } from '../../_redux/selectors';
import { StatusBadgeData } from 'enums/status';
import { DashboardProjectDto, FilterDtoStatoEnum } from '@api/client';
import { trackUserIdGa4 } from 'shared/utils/gtag';
import { TrackPagesGA4Enum } from 'enums/global.enum';
import { getDashboardProjects } from '../../_redux/actions';
import { useTranslation } from 'react-i18next';
import { DashboardFieldsColor, FilterPeriodEnum } from 'enums/dashboard.enum';
import { dashboardConfig } from '../../_config/dashboard.config';

const PAGE_SIZE = dashboardConfig.defaultPageSize;
const SERVER_SIDE_PAGINATION = dashboardConfig.projectsServerSidePagination;

const useGetDashboardProjects = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [projects, setProjects] = useState<DashboardProjectDto[] | any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isFirstRender = useRef(true);

  const projectsSelector = useAppSelector(dashboardProjectsSelector);
  const filtersUsed = useAppSelector(filtersDashboardProjectUsedSelector);

  const loadPage = useCallback(
    (page: number) => {
      setIsLoading(true);
      const projectStatus =
        filtersUsed?.projectStatus ||
        StatusBadgeData.map((el) => el.name).filter((el) => el !== FilterDtoStatoEnum.Completed);
      const filtroAnnualita = filtersUsed?.filtroAnnualita || FilterPeriodEnum.CURRENT_YEAR;

      dispatch(
        getDashboardProjects({
          dashboardProjectFilterInputDto: {
            ...filtersUsed,
            ...(SERVER_SIDE_PAGINATION
              ? { size: PAGE_SIZE, page, caricaTotali: page === 0 }
              : {}),
            projectStatus,
            filtroAnnualita,
          },
        })
      )
        .unwrap()
        .then((res) => {
          setProjects(
            res?.projects?.map((project) => ({
              ...project,
              intranetId: Number(project.intranetId),
              stato: StatusBadgeData.find((x) => x.name === project.stato)?.label,
            })) || []
          );
          if (SERVER_SIDE_PAGINATION && page === 0) {
            setTotalElements(res?.totalElements || 0);
            setTotalPages(Math.ceil((res?.totalElements || 0) / PAGE_SIZE));
          }
          setCurrentPage(page);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, filtersUsed]
  );

  useEffect(() => {
    trackUserIdGa4(TrackPagesGA4Enum.DASHBOARD_PROJECTS);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (filtersUsed) {
      loadPage(0);
    }
  }, [filtersUsed]);

  useEffect(() => {
    if (projectsSelector) {
      const projectsMapped = projectsSelector.map((project) => ({
        ...project,
        intranetId: Number(project.intranetId),
        stato: StatusBadgeData.find((x) => x.name === project.stato)?.label,
      }));
      setProjects(projectsMapped);
    }
  }, [projectsSelector]);

  const tableProps: any = {
    columns: [
      {
        title: t('dashboard.projects.table-titles.id-title'),
        key: 'intranetId',
        headerColor: '#F5F5F5',
        headerFontWeight: 'bold',
      },
      {
        title: t('dashboard.projects.table-titles.project-name-title'),
        key: 'name',
        headerColor: '#F5F5F5',
        headerFontWeight: 'bold',
      },
      {
        title: t('dashboard.projects.table-titles.status-title'),
        key: 'stato',
        headerColor: '#F5F5F5',
        headerFontWeight: 'bold',
      },
      {
        title: t('dashboard.projects.table-titles.no-plan-title'),
        key: 'prjNPRicavi',
        type: 'amount',
        headerColor: '#F5F5F5',
        headerFontWeight: 'bold',
      },
      {
        title: t('dashboard.projects.table-titles.actual-revenues-title'),
        key: 'actualRevenues',
        type: 'amount',
        color: '#c2d7df',
        headerColor: DashboardFieldsColor.REVENUES,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },

      {
        title: t('dashboard.projects.table-titles.actual-costs-title'),
        key: 'actualCosts',
        type: 'amount',
        color: '#dfb2b5',
        headerColor: DashboardFieldsColor.COSTS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.actual-hr-costs-title'),
        key: 'actualHrCosts',
        type: 'amount',
        color: '#dfb2b5',
        headerColor: DashboardFieldsColor.COSTS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.actual-other-costs-title'),
        key: 'actualOtherCosts',
        type: 'amount',
        color: '#dfb2b5',
        headerColor: DashboardFieldsColor.COSTS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.actual-margin-title'),
        key: 'actualMargin',
        type: 'amount',
        color: '#bad7b7',
        headerColor: DashboardFieldsColor.MARGINS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.actual-margin-perc-title'),
        key: 'actualMarginRate',
        type: 'perc',
        color: '#bad7b7',
        headerColor: DashboardFieldsColor.MARGINS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.actual-rate-title'),
        key: 'actualRate',
        type: 'amount',
        color: '#e990ec',
        headerColor: DashboardFieldsColor.RATE,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.af-revenues-title'),
        key: 'afRevenues',
        type: 'amount',
        color: '#c2d7df',
        headerColor: DashboardFieldsColor.REVENUES,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.af-costs-title'),
        key: 'prjAFCosti',
        type: 'amount',
        color: '#dfb2b5',
        headerColor: DashboardFieldsColor.COSTS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.af-margin-title'),
        key: 'prjAFMargin',
        type: 'amount',
        color: '#bad7b7',
        headerColor: DashboardFieldsColor.MARGINS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
      {
        title: t('dashboard.projects.table-titles.af-margin-perc-title'),
        key: 'prjAFMarginPerc',
        type: 'perc',
        color: '#bad7b7',
        headerColor: DashboardFieldsColor.MARGINS,
        textHeaderColor: '#ffffff',
        headerFontWeight: '600',
      },
    ],
  };

  return {
    projects,
    setProjects,
    tableProps,
    currentPage,
    totalElements,
    totalPages,
    isLoading,
    loadPage,
    pageSize: PAGE_SIZE,
    serverSidePagination: SERVER_SIDE_PAGINATION,
  };
};

export default useGetDashboardProjects;
